// filepath: g:\Development\Projects\familysync\frontend\src\store\slices\chatSlice.js
// File Metadata:
// Version: 1.02
// Created: 2025-04-14 00:00:00
// Last Modified: 2025-05-03
// Authors: GitHub Copilot
// Description: Redux slice for chat feature state management

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as chatDb from '../../services/db/chatDb';
import { chatApi, updateCacheOnSocketEvent } from '../api/chatApi';

// Helper function to get socket service
const getSocketService = async () => {
  try {
    return await import('../../../../services/socketService');
  } catch (error) {
    console.error('Error importing socket service:', error);
    return null;
  }
};

// Thunk to fetch all chat rooms
export const fetchChatRooms = createAsyncThunk(
  'chat/fetchRooms',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      // First try to load rooms from IndexedDB for immediate display
      const cachedRooms = await chatDb.getRooms();
      
      // Also load unread counts from IndexedDB
      const unreadCounts = await chatDb.getUnreadCounts();
      
      // Fetch fresh data from API using RTK Query
      try {
        const result = await dispatch(chatApi.endpoints.getChatRooms.initiate()).unwrap();
        const rooms = result.rooms;
        
        // Save to IndexedDB for offline access
        await chatDb.saveRooms(rooms);
        
        // If we have cached rooms, return them immediately while the API call completes
        if (cachedRooms.length > 0) {
          return { 
            isFromCache: true, 
            rooms: cachedRooms, 
            freshRooms: rooms,
            unreadCounts 
          };
        }
        
        return { isFromCache: false, rooms, unreadCounts: result.unreadCounts };
      } catch (apiError) {
        // If offline or API fails, try to return cached data
        if (cachedRooms.length > 0) {
          return { isFromCache: true, rooms: cachedRooms, unreadCounts };
        }
        throw apiError;
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch chat rooms');
    }
  }
);

// Thunk to fetch messages for a specific room
export const fetchMessages = createAsyncThunk(
  'chat/fetchMessages',
  async ({ roomId, page = 1, limit = 50 }, { rejectWithValue, dispatch }) => {
    try {
      if (!roomId) {
        return rejectWithValue('Invalid room ID provided');
      }

      // First try to load from IndexedDB for immediate display
      let cachedMessages = [];
      try {
        cachedMessages = await chatDb.getMessagesByRoom(roomId, limit, false);
      } catch (cacheError) {
        console.warn('Failed to load cached messages:', cacheError);
        // Continue executing - we'll try to fetch from server
      }
      
      // Find the most recent message timestamp in our IndexedDB cache to use for sync
      let lastMessageTimestamp = null;
      if (cachedMessages.length > 0) {
        // Sort by timestamp to find the most recent message
        const sortedMessages = [...cachedMessages].sort((a, b) => 
          new Date(b.timestamp) - new Date(a.timestamp)
        );
        lastMessageTimestamp = sortedMessages[0].timestamp;
      }
      
      // Fetch fresh data from API using RTK Query
      try {
        const result = await dispatch(
          chatApi.endpoints.getMessages.initiate({ 
            roomId, 
            page, 
            limit, 
            since: lastMessageTimestamp 
          })
        ).unwrap();
        
        const data = result;
        
        // Save transformed messages to IndexedDB
        if (data.messages && data.messages.length > 0) {
          await chatDb.saveMessages(data.messages).catch(err => {
            console.warn('Error saving messages to IndexedDB:', err);
            // Continue execution - messages are still fetched from the server
          });
        }
        
        // If we have both cached messages and new messages from server
        if (cachedMessages.length > 0 && data.messages && data.messages.length > 0) {
          // Combine the messages, avoiding duplicates
          const combinedMessages = [...cachedMessages];
          
          // Add any new messages that don't exist in our cache
          data.messages.forEach(newMsg => {
            if (!combinedMessages.some(existingMsg => existingMsg._id === newMsg._id)) {
              combinedMessages.push(newMsg);
            }
          });
          
          // Sort by timestamp
          combinedMessages.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
          
          return { 
            roomId, 
            isFromCache: false, 
            messages: combinedMessages 
          };
        }
        
        // If we only have cached messages, return them
        if (cachedMessages.length > 0 && (!data.messages || data.messages.length === 0)) {
          return { 
            roomId, 
            isFromCache: true, 
            messages: cachedMessages 
          };
        }
        
        // If we only have server messages, return them
        return { roomId, isFromCache: false, messages: data.messages || [] };
      } catch (apiError) {
        // If API call fails and we have cached messages, return those
        if (cachedMessages.length > 0) {
          return { roomId, isFromCache: true, messages: cachedMessages };
        }
        throw apiError;
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch messages');
    }
  }
);

// Thunk to send a message (fallback for when socket is unavailable)
export const sendChatMessage = createAsyncThunk(
  'chat/sendMessage',
  async ({ roomId, content, type = 'text', mediaUrl, mediaMetadata, replyTo, attachmentId }, { rejectWithValue, getState, dispatch }) => {
    try {
      // Check if we're online
      if (!navigator.onLine) {
        // Queue the message for later sending
        await chatDb.queueOfflineMessage({ roomId, content, type, mediaUrl, mediaMetadata, replyTo, attachmentId });
        
        // Create a temporary message object
        const { user } = getState();
        const currentUser = user.data;
        const tempMessage = {
          _id: 'temp_' + Date.now(),
          senderId: currentUser.id || currentUser._id,
          senderName: currentUser.name,
          senderAvatar: currentUser.avatar,
          room: roomId,
          content,
          type,
          mediaUrl,
          mediaMetadata,
          attachmentId,
          replyTo,
          timestamp: new Date().toISOString(),
          readBy: [currentUser.id || currentUser._id],
          isPending: true
        };
        
        // Save to IndexedDB to persist across page reloads
        await chatDb.saveMessage(tempMessage);
        
        return tempMessage;
      }
      
      // Use RTK Query mutation for the API call
      const result = await dispatch(
        chatApi.endpoints.sendMessage.initiate({ 
          roomId, 
          content, 
          type, 
          mediaUrl, 
          mediaMetadata, 
          replyTo, 
          attachmentId 
        })
      ).unwrap();
      
      // Save to IndexedDB for offline access
      await chatDb.saveMessage(result);
      
      return result;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to send message');
    }
  }
);

// Thunk to mark a message as read
export const markMessageAsRead = createAsyncThunk(
  'chat/markMessageAsRead',
  async ({ messageId, roomId, userId }, { rejectWithValue, dispatch, getState }) => {
    try {
      console.log('[READ RECEIPT] Redux markMessageAsRead thunk started:', { messageId, roomId, userId });
      
      // Get current user ID from the provided parameter or from state
      const currentUserId = userId || getState().user.data?._id;
      
      if (!currentUserId) {
        console.error('[READ RECEIPT] Redux markMessageAsRead - No user ID available');
        return rejectWithValue('No user ID available');
      }
      
      console.log('[READ RECEIPT] Redux markMessageAsRead - User ID:', currentUserId);
      
      // Also update in IndexedDB
      const { messagesByRoom } = getState().chat;
      if (messagesByRoom[roomId]) {
        const message = messagesByRoom[roomId].find(msg => msg._id === messageId);
        console.log('[READ RECEIPT] Redux markMessageAsRead - Found message in store:', message ? true : false);
        
        if (message) {
          console.log('[READ RECEIPT] Redux markMessageAsRead - Current readBy status:', message.readBy);
        }
        
        if (message && !message.readBy.includes(currentUserId)) {
          console.log('[READ RECEIPT] Redux markMessageAsRead - Updating IndexedDB');
          const updatedMessage = {
            ...message,
            readBy: [...message.readBy, currentUserId]
          };
          await chatDb.saveMessage(updatedMessage);
        } else if (message) {
          console.log('[READ RECEIPT] Redux markMessageAsRead - Message already read by user');
        }
      } else {
        console.log('[READ RECEIPT] Redux markMessageAsRead - Room not found in messagesByRoom');
      }
      
      // First try the socket service as it's more efficient
      try {
        console.log('[READ RECEIPT] Redux markMessageAsRead - Importing socket service');
        const socketService = await import('../../../../services/socketService');
        console.log('[READ RECEIPT] Redux markMessageAsRead - Using socket service to mark message as read');
        socketService.markMessageRead(messageId, roomId);
        return { messageId, roomId, userId: currentUserId };
      } catch (socketError) {
        console.error('[READ RECEIPT] Redux markMessageAsRead - Socket service failed:', socketError);
        
        // If socket fails, fall back to REST API using RTK Query
        console.log('[READ RECEIPT] Redux markMessageAsRead - Falling back to API request');
        await dispatch(
          chatApi.endpoints.markMessageRead.initiate({ 
            messageId, 
            roomId, 
            userId: currentUserId 
          })
        ).unwrap();
        
        console.log('[READ RECEIPT] Redux markMessageAsRead - API request successful');
        return { messageId, roomId, userId: currentUserId };
      }
    } catch (error) {
      console.error('[READ RECEIPT] Redux markMessageAsRead - Error:', error);
      return rejectWithValue(error.message || 'Failed to mark message as read');
    }
  }
);

// Thunk to react to a message
export const reactToMessage = createAsyncThunk(
  'chat/reactToMessage',
  async ({ messageId, emoji }, { rejectWithValue, getState, dispatch }) => {
    try {
      // Try WebSocket first for real-time updates
      const socketService = await getSocketService();
      const state = getState();
      const roomId = findRoomIdForMessage(state, messageId);
      
      // If we couldn't find the room, reject
      if (!roomId) {
        return rejectWithValue('Could not determine room for message');
      }
      
      const socketSuccess = socketService && socketService.reactToMessageSocket(messageId, roomId, emoji);
      
      // If WebSocket fails or is unavailable, use REST API as fallback with RTK Query
      if (!socketSuccess) {
        // Send the API request using RTK Query
        const result = await dispatch(
          chatApi.endpoints.reactToMessage.initiate({ 
            messageId, 
            emoji, 
            roomId 
          })
        ).unwrap();
        
        // Return the server response
        return result;
      }
      
      // If WebSocket was successful, return a success indicator
      // The actual state update will be handled by the socket event listener
      return { success: true, messageId, roomId };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to react to message');
    }
  }
);

// Helper function to find roomId for a message
const findRoomIdForMessage = (state, messageId) => {
  for (const [roomId, messages] of Object.entries(state.chat.messagesByRoom)) {
    const message = messages.find(msg => msg._id === messageId);
    if (message) {
      return roomId;
    }
  }
  return null;
};

// Helper function to create optimistic reaction update
const createOptimisticReactionUpdate = (state, messageId, roomId, emoji, userId) => {
  const message = state.chat.messagesByRoom[roomId]?.find(msg => msg._id === messageId);
  
  if (!message) return null;
  
  const reactions = { ...(message.reactions || {}) };
  
  // If user already reacted with this emoji, remove it (toggle behavior)
  if (reactions[emoji] && reactions[emoji].includes(userId)) {
    reactions[emoji] = reactions[emoji].filter(id => id !== userId);
    if (reactions[emoji].length === 0) {
      delete reactions[emoji];
    }
  } 
  // Otherwise add the reaction
  else {
    if (!reactions[emoji]) {
      reactions[emoji] = [];
    }
    reactions[emoji] = [...reactions[emoji], userId];
  }
  
  return {
    ...message,
    reactions,
    room: roomId
  };
};

// Thunk to sync offline messages
export const syncOfflineMessages = createAsyncThunk(
  'chat/syncOfflineMessages',
  async (_, { dispatch }) => {
    if (!navigator.onLine) return [];
    
    try {
      // Get all queued messages
      const offlineMessages = await chatDb.getOfflineMessages();
      
      // Send each message
      const results = await Promise.all(
        offlineMessages.map(async (message) => {
          try {
            // Use the existing sendChatMessage thunk
            const result = await dispatch(
              sendChatMessage({
                roomId: message.roomId,
                content: message.content,
                type: message.type
              })
            ).unwrap();
            
            // Remove from offline queue
            await chatDb.removeOfflineMessage(message.id);
            
            return { success: true, message: result };
          } catch (error) {
            return { success: false, error, message };
          }
        })
      );
      
      return results;
    } catch (error) {
      console.error('Error syncing offline messages:', error);
      return [];
    }
  }
);

// Thunk to create a group chat (parents only)
export const createGroupChat = createAsyncThunk(
  'chat/createGroupChat',
  async ({ name, participantIds }, { rejectWithValue, dispatch }) => {
    try {
      const result = await dispatch(
        chatApi.endpoints.createGroupChat.initiate({ name, participantIds })
      ).unwrap();
      
      return result;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to create group chat');
    }
  }
);

// Thunk to create a private chat (1-on-1)
export const createPrivateChat = createAsyncThunk(
  'chat/createPrivateChat',
  async ({ userId }, { rejectWithValue, dispatch }) => {
    try {
      const result = await dispatch(
        chatApi.endpoints.createPrivateChat.initiate({ userId })
      ).unwrap();
      
      // Save to IndexedDB for offline access
      await chatDb.saveRoom(result).catch(err => {
        console.warn('Error saving room to IndexedDB:', err);
      });
      
      return result;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to create private chat');
    }
  }
);

// Thunk to update a group chat (parents only)
export const updateGroupChat = createAsyncThunk(
  'chat/updateGroupChat',
  async ({ roomId, name, participantIds }, { rejectWithValue, dispatch }) => {
    try {
      const result = await dispatch(
        chatApi.endpoints.updateGroupChat.initiate({ roomId, name, participantIds })
      ).unwrap();
      
      return result;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to update group chat');
    }
  }
);

// Thunk to create or get a private chat
export const getPrivateChat = createAsyncThunk(
  'chat/getPrivateChat',
  async (participantId, { rejectWithValue, dispatch }) => {
    try {
      const result = await dispatch(
        chatApi.endpoints.getPrivateChat.initiate({ participantId })
      ).unwrap();
      return result;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to get private chat');
    }
  }
);

// Thunk to edit a message
export const editMessage = createAsyncThunk(
  'chat/editMessage',
  async ({ messageId, content }, { rejectWithValue, getState, dispatch }) => {
    try {
      // Try using WebSocket for real-time updates
      const state = getState();
      const roomId = findRoomIdForMessage(state, messageId);
      
      // If we couldn't find the room, we'll fallback to REST API only
      if (roomId) {
        const socketService = await getSocketService();
        const socketSuccess = socketService && socketService.editMessageSocket(messageId, roomId, content);
        
        // If WebSocket is successful, create an optimistic update
        if (socketSuccess) {
          // Optimistic update - we'll create a local representation of the edited message
          const message = state.chat.messagesByRoom[roomId]?.find(msg => msg._id === messageId);
          if (message) {
            return {
              ...message,
              content,
              edited: true,
              room: roomId
            };
          }
        }
      }
      
      // If WebSocket fails or isn't available, use REST API with RTK Query
      const result = await dispatch(
        chatApi.endpoints.editMessage.initiate({ 
          messageId, 
          content, 
          roomId 
        })
      ).unwrap();
      
      return result;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to edit message');
    }
  }
);

// Thunk to delete a message
export const deleteMessage = createAsyncThunk(
  'chat/deleteMessage',
  async ({ messageId, roomId }, { rejectWithValue, dispatch }) => {
    try {
      // Try using WebSocket for real-time updates first
      const socketService = await getSocketService();
      const socketSuccess = socketService && socketService.deleteMessageSocket(messageId, roomId);
      
      // If WebSocket is successful, create an optimistic update
      if (socketSuccess) {
        // Return minimal data for optimistic update
        return { messageId, roomId };
      }
      
      // If WebSocket fails or isn't available, use REST API as fallback with RTK Query
      await dispatch(
        chatApi.endpoints.deleteMessage.initiate({ 
          messageId, 
          roomId 
        })
      ).unwrap();
      
      // Return the necessary data for the reducer
      return { messageId, roomId };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to delete message');
    }
  }
);

const chatSlice = createSlice({
  name: 'chat',
  initialState: {
    chatRooms: [],
    activeRoomId: null,
    messagesByRoom: {},
    unreadCountByRoom: {},
    typingUsersByRoom: {},
    status: 'idle',
    error: null,
    activeRoomStatus: 'idle',
    activeRoomError: null,
    isOffline: !navigator.onLine,
    hasPendingMessages: false,
    connectionStatus: 'connected' // Added for connection tracking
  },
  
  reducers: {
    // Select an active chat room
    selectChatRoom: (state, action) => {
      state.activeRoomId = action.payload;
      
      // Reset unread count for the selected room
      if (action.payload && state.unreadCountByRoom[action.payload]) {
        state.unreadCountByRoom[action.payload] = 0;
        
        // Persist the updated unread count to IndexedDB
        chatDb.saveUnreadCount(action.payload, 0).catch(err => {
          console.warn('Error saving unread count to IndexedDB:', err);
        });
      }
    },
    
    // Handle a new message coming in (via WebSocket)
    receiveMessage: (state, action) => {
      const { room: roomId, _id: messageId } = action.payload;
      
      // Initialize the message array for this room if it doesn't exist
      if (!state.messagesByRoom[roomId]) {
        state.messagesByRoom[roomId] = [];
      }
      
      // Check for existing message
      const existingIndex = state.messagesByRoom[roomId].findIndex(msg => msg._id === messageId);
      
      if (existingIndex !== -1) {
        // Update existing message with new data, preserving any local state
        state.messagesByRoom[roomId][existingIndex] = {
          ...state.messagesByRoom[roomId][existingIndex],
          ...action.payload
        };
      } else {
        // Add new message to the room
        state.messagesByRoom[roomId].push(action.payload);
      }
      
      // Update the lastMessage in chatRooms
      const roomIndex = state.chatRooms.findIndex(room => room.roomId === roomId);
      if (roomIndex !== -1) {
        state.chatRooms[roomIndex].lastMessage = action.payload;
      }
      
      // Increment unread count if this room is not the active one
      if (roomId !== state.activeRoomId && action.payload.senderId !== action.payload.currentUserId) {
        const newCount = (state.unreadCountByRoom[roomId] || 0) + 1;
        state.unreadCountByRoom[roomId] = newCount;
        
        // Persist the updated unread count to IndexedDB
        chatDb.saveUnreadCount(roomId, newCount).catch(err => {
          console.warn('Error saving unread count to IndexedDB:', err);
        });
      }
    },
    
    // Set online/offline status
    setOfflineStatus: (state, action) => {
      state.isOffline = action.payload;
    },
    
    // Set pending messages flag
    setPendingMessages: (state, action) => {
      state.hasPendingMessages = action.payload;
    },
    
    // Handle typing indicator (via WebSocket)
    setUserTyping: (state, action) => {
      const { roomId, userId, name } = action.payload;
      
      if (!state.typingUsersByRoom[roomId]) {
        state.typingUsersByRoom[roomId] = [];
      }
      
      // Add user to typing list if not already there
      if (!state.typingUsersByRoom[roomId].some(user => user.userId === userId)) {
        state.typingUsersByRoom[roomId].push({ userId, name });
      }
      
      // Clear typing indicator after a delay (handled by the component)
    },
    
    // Clear typing indicator
    clearUserTyping: (state, action) => {
      const { roomId, userId } = action.payload;
      
      if (state.typingUsersByRoom[roomId]) {
        state.typingUsersByRoom[roomId] = state.typingUsersByRoom[roomId].filter(
          user => user.userId !== userId
        );
      }
    },
    
    // Update message read status (via WebSocket)
    updateMessageReadStatus: (state, action) => {
      const { messageId, roomId, userId } = action.payload;
      
      if (state.messagesByRoom[roomId]) {
        const messageIndex = state.messagesByRoom[roomId].findIndex(msg => msg._id === messageId);
        
        if (messageIndex !== -1) {
          if (!state.messagesByRoom[roomId][messageIndex].readBy.includes(userId)) {
            state.messagesByRoom[roomId][messageIndex].readBy.push(userId);
          }
        }
      }
    },
    
    // Handle room updates (via WebSocket)
    updateRoom: (state, action) => {
      const updatedRoom = action.payload;
      
      const roomIndex = state.chatRooms.findIndex(room => room.roomId === updatedRoom.roomId);
      if (roomIndex !== -1) {
        state.chatRooms[roomIndex] = {
          ...state.chatRooms[roomIndex],
          ...updatedRoom
        };
      } else {
        const exists = state.chatRooms.some(room => room.roomId === updatedRoom.roomId);
        if (!exists) {
          state.chatRooms.push(updatedRoom);
        }
      }
    },
    
    // Refresh chat state after error recovery
    refreshChatState: (state) => {
      // Reset error states
      state.error = null;
      state.activeRoomError = null;
      state.status = 'idle';
      state.activeRoomStatus = 'idle';
      
      // We don't reset the whole state as that would lose messages and rooms
      // Instead, we'll let the components refetch data as needed
    },
    
    // Track connection status for better error handling
    setConnectionStatus: (state, action) => {
      state.connectionStatus = action.payload; // 'connected', 'disconnected', 'reconnecting', 'error'
      
      // If we've reconnected, clear error states
      if (action.payload === 'connected') {
        state.error = null;
      }
    }
  },
  
  extraReducers: (builder) => {
    builder
      // Fetch chat rooms
      .addCase(fetchChatRooms.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchChatRooms.fulfilled, (state, action) => {
        state.status = 'succeeded';
        
        if (action.payload.isFromCache) {
          // If we have fresh data as well, use that
          if (action.payload.freshRooms) {
            state.chatRooms = action.payload.freshRooms;
          } else {
            state.chatRooms = action.payload.rooms;
          }
        } else {
          state.chatRooms = action.payload.rooms;
        }
        
        // Initialize unread counts from rooms data first
        state.chatRooms.forEach(room => {
          state.unreadCountByRoom[room.roomId] = room.unreadCount || 0;
        });
        
        // Override with persisted unread counts from IndexedDB if available
        if (action.payload.unreadCounts) {
          // Merge the persisted unread counts with the state
          state.unreadCountByRoom = {
            ...state.unreadCountByRoom,
            ...action.payload.unreadCounts
          };
        }
      })
      .addCase(fetchChatRooms.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      
      // Fetch messages
      .addCase(fetchMessages.pending, (state) => {
        state.activeRoomStatus = 'loading';
      })
      .addCase(fetchMessages.fulfilled, (state, action) => {
        state.activeRoomStatus = 'succeeded';
        const { roomId, messages, isFromCache, freshMessages } = action.payload;
        
        // Create a map of existing messages by ID for efficient lookup
        const existingMessagesMap = {};
        if (state.messagesByRoom[roomId]) {
          state.messagesByRoom[roomId].forEach(msg => {
            existingMessagesMap[msg._id] = msg;
          });
        }
        
        // Merge messages avoiding duplicates
        let mergedMessages;
        if (isFromCache && freshMessages) {
          // Use fresh messages as the base, but keep any local state for existing messages
          mergedMessages = freshMessages.map(msg => 
            existingMessagesMap[msg._id] ? { ...existingMessagesMap[msg._id], ...msg } : msg
          );
        } else {
          // Merge incoming messages with existing ones, preserving local state
          mergedMessages = [];
          
          // Start with existing messages that aren't in the incoming set
          if (state.messagesByRoom[roomId]) {
            const incomingIds = new Set(messages.map(m => m._id));
            state.messagesByRoom[roomId].forEach(msg => {
              if (!incomingIds.has(msg._id)) {
                mergedMessages.push(msg);
              }
            });
          }
          
          // Add all incoming messages, updating any that already exist
          messages.forEach(msg => {
            if (existingMessagesMap[msg._id]) {
              // Merge with existing message data to preserve local state
              mergedMessages.push({ ...existingMessagesMap[msg._id], ...msg });
            } else {
              mergedMessages.push(msg);
            }
          });
        }
        
        // Sort by timestamp
        mergedMessages.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
        
        // Update state
        state.messagesByRoom[roomId] = mergedMessages;
        
        // Reset unread count for this room
        state.unreadCountByRoom[roomId] = 0;
      })
      .addCase(fetchMessages.rejected, (state, action) => {
        state.activeRoomStatus = 'failed';
        state.activeRoomError = action.payload;
      })
      
      // Send message fallback
      .addCase(sendChatMessage.fulfilled, (state, action) => {
        const roomId = action.payload.room;
        
        // Initialize the message array for this room if it doesn't exist
        if (!state.messagesByRoom[roomId]) {
          state.messagesByRoom[roomId] = [];
        }
        
        // If this is replacing a temporary message, remove it
        if (action.payload._id && !action.payload._id.startsWith('temp_')) {
          state.messagesByRoom[roomId] = state.messagesByRoom[roomId].filter(
            msg => !msg._id.startsWith('temp_') || (msg.content !== action.payload.content)
          );
        }
        
        // Add the message to the room if it's not a duplicate
        if (!state.messagesByRoom[roomId].some(msg => msg._id === action.payload._id)) {
          state.messagesByRoom[roomId].push(action.payload);
        }
        
        // Update the lastMessage in chatRooms
        const roomIndex = state.chatRooms.findIndex(room => room.roomId === roomId);
        if (roomIndex !== -1) {
          state.chatRooms[roomIndex].lastMessage = action.payload;
        }
      })
      
      // Sync offline messages
      .addCase(syncOfflineMessages.fulfilled, (state, action) => {
        // Update pending messages flag if needed
        state.hasPendingMessages = action.payload.some(result => !result.success);
      })
      
      // Mark message as read
      .addCase(markMessageAsRead.fulfilled, (state, action) => {
        const { messageId, roomId } = action.payload;
        
        if (state.messagesByRoom[roomId]) {
          const messageIndex = state.messagesByRoom[roomId].findIndex(msg => msg._id === messageId);
          
          if (messageIndex !== -1 && state.messagesByRoom[roomId][messageIndex]) {
            if (!state.messagesByRoom[roomId][messageIndex].readBy.includes(action.meta.arg.userId)) {
              state.messagesByRoom[roomId][messageIndex].readBy.push(action.meta.arg.userId);
            }
          }
        }
      })
      
      // Create group chat
      .addCase(createGroupChat.fulfilled, (state, action) => {
        const exists = state.chatRooms.some(room => room.roomId === action.payload.roomId);
        if (!exists) {
          state.chatRooms.push({
            ...action.payload,
            unreadCount: 0,
            displayName: action.payload.name,
            avatarUrl: null // Could set a default group icon
          });
        }
      })
      
      // Update group chat
      .addCase(updateGroupChat.fulfilled, (state, action) => {
        const roomIndex = state.chatRooms.findIndex(room => room.roomId === action.payload.roomId);
        if (roomIndex !== -1) {
          state.chatRooms[roomIndex] = {
            ...state.chatRooms[roomIndex],
            ...action.payload,
            displayName: action.payload.name
          };
        }
      })
      
      // Get private chat
      .addCase(getPrivateChat.fulfilled, (state, action) => {
        const roomExists = state.chatRooms.some(room => room.roomId === action.payload.roomId);
        
        if (!roomExists) {
          state.chatRooms.push({
            ...action.payload,
            unreadCount: 0,
            // Display name will be set once user info is loaded
            displayName: 'Private Chat',
            avatarUrl: null
          });
        }
        
        state.activeRoomId = action.payload.roomId;
      })
      
      // Handle createPrivateChat states
      .addCase(createPrivateChat.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(createPrivateChat.fulfilled, (state, action) => {
        state.status = 'succeeded';
        const roomExists = state.chatRooms.some(room => room.roomId === action.payload.roomId);
        
        if (!roomExists) {
          state.chatRooms.push({
            ...action.payload,
            unreadCount: 0,
            displayName: 'Private Chat',
            avatarUrl: null
          });
        }
        
        state.activeRoomId = action.payload.roomId;
      })
      .addCase(createPrivateChat.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      
      // Handle message reaction
      .addCase(reactToMessage.fulfilled, (state, action) => {
        console.log('Reacted to message:', action.payload);
        const updatedMsg = action.payload;
        const roomId = updatedMsg.room;
        if (state.messagesByRoom[roomId]) {
          const idx = state.messagesByRoom[roomId].findIndex(msg => msg._id === updatedMsg._id);
          if (idx !== -1) {
            state.messagesByRoom[roomId][idx] = {
              ...state.messagesByRoom[roomId][idx],
              ...updatedMsg
            };
          }
        }
      })
      
      // Handle message edit
      .addCase(editMessage.fulfilled, (state, action) => {
        const updatedMsg = action.payload;
        const roomId = updatedMsg.room;
        if (state.messagesByRoom[roomId]) {
          const idx = state.messagesByRoom[roomId].findIndex(msg => msg._id === updatedMsg._id);
          if (idx !== -1) {
            state.messagesByRoom[roomId][idx] = {
              ...state.messagesByRoom[roomId][idx],
              ...updatedMsg
            };
          }
        }
      })
      
      // Handle message delete
      .addCase(deleteMessage.fulfilled, (state, action) => {
        const { messageId, roomId } = action.payload;
        if (state.messagesByRoom[roomId]) {
          const idx = state.messagesByRoom[roomId].findIndex(msg => msg._id === messageId);
          if (idx !== -1) {
            state.messagesByRoom[roomId][idx] = {
              ...state.messagesByRoom[roomId][idx],
              deleted: true,
              content: '',
              edited: false
            };
          }
        }
      });
  }
});

export const {
  selectChatRoom,
  receiveMessage,
  setUserTyping,
  clearUserTyping,
  updateMessageReadStatus,
  updateRoom,
  setOfflineStatus,
  setPendingMessages,
  refreshChatState,
  setConnectionStatus
} = chatSlice.actions;

export default chatSlice.reducer;